import { SystemStyleObject } from "@chakra-ui/theme-tools";
import { focusRing } from "../focusStyles";

export const textStyles: Record<string, SystemStyleObject> = {
  /*
   * Rich content has distinct styling requirements, and there are no defaults
   * configured in Chakra.
   * Always apply this textStyle to a Box (i.e. a plain div). Don't apply to a
   * VStack/Stack or another component, as it can have conflicting styling.
   */
  richContent: {
    lineHeight: 1.6,

    // remove all default margin spacing
    "& > *": {
      marginTop: 0,
      marginBottom: 0,
    },

    // the efault spacing between top-level markdown blocks.
    "& > * + *": {
      marginTop: 4,
    },

    // larger spacing before a new section.
    "& > * + h2": {
      marginTop: 10,
    },

    // medium spacing before subsections.
    "& > * + h3": {
      marginTop: 7,
    },

    // smaller spacing before minor subsections.
    "& > * + h4": {
      marginTop: 5,
    },

    // tighter spacing immediately after headings.
    "& > h2 + *": {
      marginTop: 6,
    },

    "& > h3 + *": {
      marginTop: 2,
    },

    // tighter spacing immediately after h4 headings.
    "& > h4 + *": {
      marginTop: 2,
    },

    // extra spacing after a top-level list before the next top-level block
    "& > ol + *, & > ul + *": {
      marginTop: 8,
    },

    "& h1": {
      fontSize: "2xl",
      fontWeight: "bold",
      lineHeight: 1.25,
    },

    "& h2": {
      fontSize: "xl",
      fontWeight: "bold",
      lineHeight: 1.3,
    },

    "& h3": {
      fontSize: "lg",
      fontWeight: "semibold",
      lineHeight: 1.35,
    },

    "& h4": {
      fontSize: "md",
      fontWeight: "semibold",
      lineHeight: 1.4,
    },

    // top-level ordered list
    // we override the default rendering or the number
    // with our own custom one, defined in css only.

    "& > ol": {
      listStyle: "none",
      counterReset: "item",
      paddingLeft: 0,
    },

    "& > ol > li": {
      counterIncrement: "item",
      position: "relative",
      paddingLeft: "2em",
    },

    "& > ol > li::before": {
      content: "counter(item)",
      position: "absolute",
      left: 0,
      top: "0.2em",

      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",

      width: "1.7em",
      height: "1.7em",
      borderRadius: "full",
      bg: "primary.50",
      color: "primary.600",
      fontSize: "0.85em",
      fontWeight: "bold",
      lineHeight: 1,
    },

    // a nested ordered list doesn't have special styling and
    // shouldn't really be used
    "& ol ol": {
      paddingLeft: 5,
    },

    "& ol ol > li": {
      counterIncrement: "none",
      paddingLeft: 0,
    },

    "& ol ol > li::before": {
      content: "none",
    },

    // unordered list uses a solid circle
    "& ul": {
      listStyleType: "disc",
      paddingLeft: 5,
    },

    "& ul li::marker": {
      color: "primary.400",
    },

    // spacing between top-level list items
    "& > ol > li + li, & > ul > li + li": {
      marginTop: 4,
    },

    // spacing between block children inside a list item, such as nested alerts.
    // specifically, don't target the inline code elements
    "& li > :where(p, div, ul, ol, blockquote, pre) + :where(p, div, ul, ol, blockquote, pre)":
      {
        marginTop: 3,
      },

    // space before entering a nested list, and after returning to parent flow.
    "& li > ul, & li > ol": {
      marginTop: 2,
      marginBottom: 4,
    },

    // nested list items should be tighter than top-level steps.
    "& li li + li": {
      marginTop: 2,
    },

    // paragraph text
    "& p": {
      overflowWrap: "anywhere",
      wordBreak: "normal",
      whiteSpace: "normal",
    },

    // links
    "& a": {
      fontWeight: "bold",
      textDecorationLine: "underline",
      textDecorationColor: "primary.300",
      textDecorationThickness: "2px",
      textUnderlineOffset: "3px",
      borderRadius: "sm",
    },

    "& a:hover": {
      color: "gray.900",
      textDecorationColor: "primary.700",
    },

    "& a:focus": { outline: "none" },

    "& a:focus-visible": focusRing,

    // strong, ** **
    "& strong": {
      color: "gray.900",
      fontWeight: "bold",
    },

    // emphasis, * *
    "& em": {
      fontStyle: "italic",
      fontWeight: "semibold",
    },

    // inline code, ` `
    "& code": {
      display: "inline-block",
      width: "fit-content",
      bg: "gray.50",
      paddingLeft: 1.5,
      paddingRight: 1.5,
      paddingTop: 0.5,
      paddingBottom: 0.5,
      fontFamily: "mono",
      fontSize: "0.85em",
      fontWeight: "normal",
    },

    // code block, ``` ```
    "& pre": {
      display: "block",
      width: "full",
      overflowX: "auto",
      overflowY: "hidden",
      whiteSpace: "pre",
      padding: 4,
      borderRadius: "md",
      bg: "gray.100",
      fontSize: "sm",
      lineHeight: 1.6,
    },

    // blockquote, >
    "& blockquote": {
      marginLeft: 0,
      paddingTop: 2.5,
      paddingBottom: 2.5,
      paddingLeft: 3,
      paddingRight: 3,
      borderLeftWidth: "3px",
      borderLeftColor: "primary.300",
      borderRadius: "md",
      bg: "primary.25",
    },

    "& blockquote > p:first-of-type": {
      marginTop: 0,
    },

    "& blockquote > p:last-of-type": {
      marginBottom: 0,
    },

    "& hr": {
      width: "full",
      height: 0,
      border: 0,
      borderTopWidth: "1px",
      borderTopColor: "gray.300",
    },
  },
};
